import * as THREE from 'three';

import type { TerrainTileMesh } from '../../types/terrain-types.js';

interface TileEntry {
  mesh: TerrainTileMesh;
  bounds: THREE.Box3;
}

interface TileNode {
  bounds: THREE.Box3;
  entries?: TileEntry[];
  left?: TileNode;
  right?: TileNode;
}

/** XY broad phase for vertical terrain rays; rebuilt only when resident tiles change. */
export class TerrainHeightIndex {
  private readonly tiles = new Map<TerrainTileMesh, TileEntry>();
  private root: TileNode | undefined;
  private dirty = false;
  private readonly raycaster = new THREE.Raycaster();
  private readonly down = new THREE.Vector3(0, 0, -1);
  private readonly rayOrigin = new THREE.Vector3();
  private readonly hits: THREE.Intersection[] = [];

  add(mesh: TerrainTileMesh): THREE.Box2 {
    mesh.updateWorldMatrix(true, false);
    if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox();
    const localBounds = mesh.geometry.boundingBox;
    if (!localBounds) throw new Error('Terrain geometry has no bounding box');
    const bounds = localBounds.clone().applyMatrix4(mesh.matrixWorld);
    this.tiles.set(mesh, { mesh, bounds });
    this.root = undefined;
    this.dirty = true;
    return xyBounds(bounds);
  }

  remove(mesh: TerrainTileMesh): THREE.Box2 | undefined {
    const entry = this.tiles.get(mesh);
    if (!entry) return undefined;
    this.tiles.delete(mesh);
    this.root = undefined;
    this.dirty = true;
    return xyBounds(entry.bounds);
  }

  sample(x: number, y: number): { zoom: number; z: number } | null {
    if (this.dirty) {
      this.root = buildNode([...this.tiles.values()]);
      this.dirty = false;
    }

    const candidates: TileEntry[] = [];
    collectAt(this.root, x, y, candidates);
    candidates.sort((a, b) => b.mesh.userData.zoom - a.mesh.userData.zoom);

    let best: { zoom: number; z: number } | null = null;
    for (const { mesh, bounds } of candidates) {
      const zoom = mesh.userData.zoom;
      if (best && zoom < best.zoom) break;

      // Start above this tile, including unusually high terrain and translated origins.
      this.rayOrigin.set(x, y, bounds.max.z + 1);
      this.raycaster.set(this.rayOrigin, this.down);
      this.hits.length = 0;
      this.raycaster.intersectObject(mesh, false, this.hits);
      const hit = this.hits[0];
      if (hit && (!best || hit.point.z > best.z)) {
        best = { zoom, z: hit.point.z };
      }
    }
    this.hits.length = 0;
    return best;
  }
}

function xyBounds(bounds: THREE.Box3): THREE.Box2 {
  return new THREE.Box2(
    new THREE.Vector2(bounds.min.x, bounds.min.y),
    new THREE.Vector2(bounds.max.x, bounds.max.y),
  );
}

function buildNode(entries: TileEntry[]): TileNode | undefined {
  if (entries.length === 0) return undefined;
  const bounds = new THREE.Box3();
  for (const entry of entries) bounds.union(entry.bounds);
  if (entries.length <= 4) return { bounds, entries };

  const axis = bounds.max.x - bounds.min.x >= bounds.max.y - bounds.min.y ? 'x' : 'y';
  entries.sort(
    (a, b) => a.bounds.min[axis] + a.bounds.max[axis] - b.bounds.min[axis] - b.bounds.max[axis],
  );
  const middle = Math.floor(entries.length / 2);
  return {
    bounds,
    left: buildNode(entries.slice(0, middle)),
    right: buildNode(entries.slice(middle)),
  };
}

function collectAt(node: TileNode | undefined, x: number, y: number, result: TileEntry[]): void {
  if (!node || !containsXY(node.bounds, x, y)) return;
  if (node.entries) {
    for (const entry of node.entries) {
      if (containsXY(entry.bounds, x, y)) result.push(entry);
    }
  } else {
    collectAt(node.left, x, y, result);
    collectAt(node.right, x, y, result);
  }
}

function containsXY(bounds: THREE.Box3, x: number, y: number): boolean {
  return x >= bounds.min.x && x <= bounds.max.x && y >= bounds.min.y && y <= bounds.max.y;
}
